import React from 'react';
import { CertificateTemplate, CertificateTextField } from '../../../apis/api-client';
import { Box, Dialog, DialogContent, IconButton } from '../../ui';
import { CloseIcon } from '../../icons';

/**
 * Live-preview modal — renders the certificate exactly as a learner will
 * receive it. Same field positions and font settings as the editor
 * canvas, but no drag handles, no edit chrome, scaled to the dialog's
 * content width. Useful for the final visual check before saving —
 * particularly for catching field overlaps or text running past the seal.
 */
export default function CertificatePreviewModal({
  open,
  onClose,
  template,
  backgroundUrl,
  liveValueForField,
  previewModalRef,
  previewWidth,
}: {
  open: boolean;
  onClose: () => void;
  template: CertificateTemplate;
  backgroundUrl: string | null;
  liveValueForField: (field: CertificateTextField) => string;
  previewModalRef: (node: HTMLDivElement | null) => void;
  previewWidth: number;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'grey.100' } }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          aria-label="Close preview"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            bgcolor: 'rgba(255,255,255,0.9)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            '&:hover': { bgcolor: '#fff' },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          ref={previewModalRef}
          sx={{
            width: '100%',
            p: 2,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {(() => {
            // Match the editor canvas's coordinate system but compute
            // a fresh scale against the dialog's content width so
            // the preview fills the modal without inheriting the
            // editor's scale (which is tuned for the in-page column).
            if (!previewWidth || !template.width) return null;
            const previewScale = previewWidth / template.width;
            const previewHeight = template.height * previewScale;
            return (
              <Box
                sx={{
                  position: 'relative',
                  width: previewWidth,
                  height: previewHeight,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                }}
              >
                {backgroundUrl && (
                  <Box
                    component="img"
                    src={backgroundUrl}
                    alt="Certificate background"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.style.display = 'none';
                    }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}
                {template.textFields.map((field) => {
                  const text = liveValueForField(field);
                  const previewFontSize = field.fontSize * previewScale;

                  // field.x/field.y are the text's true top-left — the editor's
                  // selection outline and padding no longer shift the text, so
                  // there's no chrome offset to undo (this is what makes the
                  // coordinate identical to the server render). For center/right
                  // alignment the editor anchors off an estimated width
                  // (chars × size × 0.6), so we mirror that same estimate here.
                  const estWidth = text.length * previewFontSize * 0.6;
                  let left = field.x * previewScale;
                  if (field.textAlign === 'center') left -= estWidth / 2;
                  else if (field.textAlign === 'right') left -= estWidth;

                  return (
                    <Box
                      key={field.fieldType}
                      sx={{
                        position: 'absolute',
                        left,
                        top: field.y * previewScale,
                        fontSize: previewFontSize,
                        fontFamily: field.fontFamily,
                        color: field.fontColor,
                        fontWeight: field.fontWeight === 'bold' ? 700 : 400,
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                        pointerEvents: 'none',
                      }}
                    >
                      {text}
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
